import React from 'react';
import { formatQuoteMoney } from '../../utils/quotePricing';
import { MoneyFigures } from '../../utils/moneyPresentation';

interface MoneySummaryProps {
  /** proposal: before acceptance. glance: the short account shown beside the Pay button. */
  mode: 'proposal' | 'glance';
  currency: string;
  quoteTotalMinor: number;
  lockedZarMinor: number;
  depositMinor: number;
  depositPercent?: number;
  figures?: MoneyFigures | null;
}

const Row = ({ label, value, total = false }: { label: string; value: string; total?: boolean }) => (
  <div className={total ? 'quote-total' : 'quote-amount-row'}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

// Three plain terms, used the same way everywhere: Price, Deposit, Balance.
// Each number appears once.
const MoneySummary = ({
  mode,
  currency,
  quoteTotalMinor,
  lockedZarMinor,
  depositMinor,
  depositPercent,
  figures,
}: MoneySummaryProps) => {
  const depositLabel = depositPercent != null
    ? `Deposit (${depositPercent}%) to secure your date`
    : 'Deposit to secure your date';
  const balanceLabel = 'Balance, payable on completion';
  const foreign = currency !== 'R';

  if (mode === 'proposal') {
    const remainder = Math.max(0, lockedZarMinor - depositMinor);
    return (
      <div className="money-summary">
        <Row total label="Price" value={formatQuoteMoney(quoteTotalMinor, currency)} />
        {foreign ? <Row label="Price in rand" value={formatQuoteMoney(lockedZarMinor, 'R')} /> : null}
        <Row label={depositLabel} value={formatQuoteMoney(depositMinor, 'R')} />
        <Row label={balanceLabel} value={formatQuoteMoney(remainder, 'R')} />
      </div>
    );
  }

  const totalMinor = figures?.totalMinor || lockedZarMinor;
  const paidMinor = figures?.receivedMinor ?? 0;
  const outstandingMinor = figures?.outstandingMinor ?? Math.max(0, totalMinor - paidMinor);
  const depositAmountMinor = figures?.depositRequiredMinor || depositMinor;

  // Nothing paid yet: show the two payments that make up the price.
  if (paidMinor <= 0) {
    return (
      <div className="money-summary quote-glance">
        <Row label={depositPercent != null ? `Deposit (${depositPercent}%)` : 'Deposit'} value={formatQuoteMoney(Math.min(depositAmountMinor, totalMinor), 'R')} />
        <Row label={balanceLabel} value={formatQuoteMoney(Math.max(0, totalMinor - depositAmountMinor), 'R')} />
      </div>
    );
  }

  // Something has been paid: what is left is all that matters.
  return (
    <div className="money-summary quote-glance">
      <Row label="Paid so far" value={formatQuoteMoney(paidMinor, 'R')} />
      <Row total label="Still to pay" value={formatQuoteMoney(outstandingMinor, 'R')} />
    </div>
  );
};

export default MoneySummary;
